import { useState } from "react";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { defineMessages, useIntl } from "react-intl";
import { classNames } from "@ct-react/core";
import { useLocaleFormatter, useLocaleNavigate } from "@ct-react/locale";
import { AnyCartItem, isRentalAccommodationCartItem } from "@shared/graphql";
import { actions, booking } from "../../i18n/sharable-defs";
import { DataProps } from "../../tools/components";
import { useTools } from "../../hooks/tools";
import { useCheckoutContext } from "../../pages/checkout/outlet";
import RatioImage from "../medias/ratio-image";
import Button from "../common/button";
import CartPrice from "./price";
import { CountdownInfo } from "../../bundles/client-ui";
import "./item.scss";

const REMOVE_CART_ITEM_GQL_MUT = gql`
  mutation RemoveCartItem($sessionId: String, $userId: String, $item: UUID!) {
    deletion: cartRemoveItem(sessionId: $sessionId, userId: $userId, item: $item) {
      cartId
      allowsMultiItems
      fullPrice { amount discount }
    }
  }
`;

const transDefs = defineMessages({
  stay: { id: "cart.item.nights-label", defaultMessage: "{count, plural, =1 {Nuitée} other {Nuitées}}" },
  period: { id: "cart.item.period-label", defaultMessage: "Dates sélectionnées" }
});

const CartItem = ({ className, data }: DataProps<AnyCartItem>) => {

  const intl = useIntl();
  const [ { sessionId, userId } ] = useCheckoutContext();
  const navigate = useLocaleNavigate();
  const { print } = useLocaleFormatter();
  const { format } = useTools();

  const [ onRemoving, setOnRemoving ] = useState(false);

  const [ removeFromCart ] = useMutation<any>(REMOVE_CART_ITEM_GQL_MUT, {
    variables: { sessionId, userId },
    update: (cache, { data }) => {
      if (!data) return;
      const { deletion: { cartId, itemId, allowsMultiItems, fullPrice } } = data;
      cache.modify({
        id: cache.identify({ __ref: `Cart:${cartId}` }),
        fields: {
          //@ts-ignore
          items: (cached: AnyCartItem[] = []) => allowsMultiItems ? cached.filter(item => item.itemId !== itemId) : [],
          itemsCount: (cached: number = 0) => allowsMultiItems ? cached - 1 : 0,
          fullPrice: () => fullPrice
        }
      });
    }
  });

  const onRemove = (item: string) => {
    setOnRemoving(true);
    removeFromCart({ variables: { item } }).then(() => setOnRemoving(false));
  }

  const wrapperClasses = classNames("one-cart-item", className);

  return (
    <article className={wrapperClasses}>
      <div className="two-col">

        <div className="one-item-resume">
          {!!data.image &&
            <RatioImage className="preview" source={data?.image} ratio="16/9" alt={data.title} />
          }
          <div>
            <h1>{data.title}</h1>
            {!!data.description && <span className="description">{data.description}</span>}
          </div>
          {isRentalAccommodationCartItem(data) &&
            <dl className="period">
              <dt>{intl.formatMessage(transDefs.period)}</dt>
              <dd>{print.period(data.period, true)}</dd>
              <dt>{intl.formatMessage(transDefs.stay, { count: format.nights(data.period) })}</dt>
              <dd>{intl.formatMessage(booking.nights, { count: format.nights(data.period) })}</dd>
            </dl>
          }
          {!!data.expirationDate && <CountdownInfo expirationDate={data.expirationDate}
                                                   onExpiration={() => navigate(0)} />}
          <Button className="removal primary"
                  processing={onRemoving}
                  onClick={() => onRemove(data.itemId)}>
            {intl.formatMessage(actions.remove)}
          </Button>
        </div>

        <div className="one-item-price spacing">
          <CartPrice data={{price: data.price, downPayment: data.downPayment }} />
        </div>

      </div>
    </article>);

};

export default CartItem;
